import React, { useState, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import './SpecWriter.css';
import {
  Phase,
  ChatMessage,
  UiMessage,
  UploadedFile,
  Spec,
  ChatResponse,
  SubmitResponse,
} from './types';

interface User {
  name: string;
  email: string;
  account: any;
  isExternal?: boolean;
  token?: string;
}

interface Props {
  user: User;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
const API_BASE = `${BACKEND_URL}/api/spec-writer`;
const MAX_FILE_SIZE_MB = 10;
const ALLOWED_TYPES = ['.pdf', '.doc', '.docx', '.png', '.jpg', '.jpeg', '.xlsx', '.pptx'];
const WELCOME_MESSAGE =
  "I'm here to help you submit a request. Just describe what you need in plain language, and I'll turn it into a structured spec for the team.";
const ASSISTANT_NAME = 'CellColabs Assistant';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

function computeInitials(name: string): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

const IconAttach = () => (
  <svg viewBox="0 0 24 24"><path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" /></svg>
);
const IconSend = () => (
  <svg viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>
);
const IconClose = () => (
  <svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
);
const IconFile = () => (
  <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z" /></svg>
);
const IconGlobe = () => (
  <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" /></svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
);
const IconWarning = () => (
  <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
);
const IconSpec = () => (
  <svg viewBox="0 0 24 24">
    <path d="M19.5 3.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2v14H3v3c0 1.66 1.34 3 3 3h12c1.66 0 3-1.34 3-3V2l-1.5 1.5zM15 20H6c-.55 0-1-.45-1-1v-1h10v2zm4-1c0 .55-.45 1-1 1s-1-.45-1-1v-3H8V5h11v14z" />
    <path d="M9 7h6v2H9zm0 3h6v2H9z" />
  </svg>
);

const SpecWriter: React.FC<Props> = ({ user }) => {
  const { instance } = useMsal();
  const [phase, setPhase] = useState<Phase>('CONVERSING');
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [spec, setSpec] = useState<Spec | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [reference, setReference] = useState('');
  const [lastUserMessage, setLastUserMessage] = useState('');

  const messagesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const firstName = user.name ? user.name.split(' ')[0] : '';
    const greeting = firstName ? `Hi ${firstName}! ` : 'Hi! ';
    setMessages([{ type: 'ai', text: greeting + WELCOME_MESSAGE }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, isTyping, phase]);

  const getAuthHeaders = async (): Promise<Record<string, string>> => {
    if (user.isExternal) {
      const token = user.token || sessionStorage.getItem('externalUserToken') || '';
      return {
        Authorization: `Bearer ${token}`,
        'x-external-user': 'true',
      };
    }
    const response = await instance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: user.account,
    });
    return { Authorization: `Bearer ${response.accessToken}` };
  };

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const validateAndAttachFiles = (files: File[]) => {
    const accepted: File[] = [];
    for (const f of files) {
      if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        alert(`${f.name} exceeds the ${MAX_FILE_SIZE_MB} MB limit.`);
        continue;
      }
      const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_TYPES.includes(ext)) {
        alert(`File type ${ext} is not allowed.`);
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length) setAttachedFiles((prev) => [...prev, ...accepted]);
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    validateAndAttachFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pasted: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind !== 'file') continue;
      const blob = item.getAsFile();
      if (!blob) continue;
      // Clipboard blobs (esp. screenshots) arrive without meaningful filenames
      // — Chromium gives them 'image.png'. Stamp a readable timestamped name so
      // the chip + Monday attachment aren't opaque.
      const mimeExt = (blob.type.split('/')[1] || 'bin').toLowerCase();
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const isGenericName = !blob.name || blob.name === 'image.png';
      const name = isGenericName ? `pasted-${stamp}.${mimeExt}` : blob.name;
      pasted.push(new File([blob], name, { type: blob.type }));
    }
    if (pasted.length === 0) return;
    e.preventDefault();
    validateAndAttachFiles(pasted);
  };

  const removeAttachment = (idx: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const uploadFile = async (file: File): Promise<UploadedFile | null> => {
    try {
      const headers = await getAuthHeaders();
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API_BASE}/upload`, form, { headers });
      return {
        fileId: res.data.fileId,
        fileName: res.data.fileName,
        fileSize: res.data.fileSize,
        mimeType: res.data.mimeType,
      };
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed for ' + file.name);
      return null;
    }
  };

  const handleSend = async () => {
    const message = inputValue.trim();
    if (!message || isTyping) return;

    const uploaded: UploadedFile[] = [];
    for (const f of attachedFiles) {
      const u = await uploadFile(f);
      if (u) uploaded.push(u);
    }
    const nextUploaded = [...uploadedFiles, ...uploaded];
    setUploadedFiles(nextUploaded);
    setAttachedFiles([]);

    const userMsg: UiMessage = { type: 'user', text: message };
    if (uploaded.length) userMsg.files = uploaded;
    setMessages((prev) => [...prev, userMsg]);
    setLastUserMessage(message);
    setInputValue('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setIsTyping(true);
    setErrorMessage('');

    const nextHistory: ChatMessage[] = [...history, { role: 'user', content: message }];
    setHistory(nextHistory);

    try {
      const headers = await getAuthHeaders();
      const res = await axios.post<ChatResponse>(
        `${API_BASE}/chat`,
        {
          message,
          files: nextUploaded.map((u) => u.fileId),
          history: nextHistory,
        },
        { headers }
      );
      const reply = res.data.reply || '';
      const specUpdate = res.data.specUpdate;
      const aiMsg: UiMessage = { type: 'ai', text: reply };
      if (specUpdate?.domain) aiMsg.domain = specUpdate.domain;
      setMessages((prev) => [...prev, aiMsg]);
      setHistory((prev) => [...prev, { role: 'assistant', content: reply }]);
      if (specUpdate) setSpec(specUpdate);
      setIsTyping(false);
    } catch (err) {
      console.error('Chat failed:', err);
      setIsTyping(false);
      setPhase('ERROR');
      setErrorMessage("I couldn't process your message. This is usually temporary.");
    }
  };

  const handleRetry = () => {
    setPhase('CONVERSING');
    setErrorMessage('');
    if (lastUserMessage) setInputValue(lastUserMessage);
  };

  const handleSubmit = async () => {
    if (!spec || spec.status !== 'ready') return;
    setPhase('SUBMITTING');
    try {
      const headers = await getAuthHeaders();
      const res = await axios.post<SubmitResponse>(
        `${API_BASE}/submit`,
        {
          spec,
          attachments: uploadedFiles.map((u) => u.fileId),
        },
        { headers }
      );
      setReference(res.data.reference || '');
      setPhase('CONFIRMED');
    } catch (err: any) {
      console.error('Submit failed:', err);
      const detail = err?.response?.data?.error || err.message || 'Unknown error';
      setErrorMessage('Submission failed (' + detail + ')');
      setPhase('ERROR');
    }
  };

  const handleNewRequest = () => {
    setPhase('CONVERSING');
    setHistory([]);
    setSpec(null);
    setAttachedFiles([]);
    setUploadedFiles([]);
    setInputValue('');
    setReference('');
    setLastUserMessage('');
    setErrorMessage('');
    const firstName = user.name ? user.name.split(' ')[0] : '';
    const greeting = firstName ? `Hi ${firstName}! ` : 'Hi! ';
    setMessages([{ type: 'ai', text: greeting + WELCOME_MESSAGE }]);
  };

  const canSend = inputValue.trim().length > 0 && !isTyping;
  const canSubmit = spec?.status === 'ready' && phase === 'CONVERSING';

  const renderMessageContent = (msg: UiMessage) => {
    const paragraphs = (msg.text || '').split(/\n\n+/);
    return paragraphs.map((p, i) => {
      const parts = p.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i}>
          {parts.map((part, j) =>
            part.startsWith('**') && part.endsWith('**') ? (
              <strong key={j}>{part.slice(2, -2)}</strong>
            ) : (
              <span key={j}>{part}</span>
            )
          )}
        </p>
      );
    });
  };

  const renderPreviewBody = () => {
    if (phase === 'CONFIRMED') {
      return (
        <div className="sw-preview__body">
          <div className="sw-confirmation">
            <div className="sw-confirmation__icon"><IconCheck /></div>
            <h4 className="sw-confirmation__heading">Spec submitted</h4>
            <p className="sw-confirmation__text">
              We've received your request. You'll get an email confirmation shortly.
            </p>
            {reference && <div className="sw-confirmation__ref">{reference}</div>}
            <button className="sw-confirmation__new" onClick={handleNewRequest}>
              Submit another request
            </button>
          </div>
        </div>
      );
    }

    if (!spec) {
      return (
        <div className="sw-preview__body">
          <div className="sw-preview__empty">
            <div className="sw-preview__empty-icon"><IconSpec /></div>
            <p className="sw-preview__empty-text">
              Your spec will build here as the conversation progresses
            </p>
          </div>
        </div>
      );
    }

    const placeholder = 'Awaiting details...';
    return (
      <>
        <div className="sw-preview__body">
          <div className="sw-spec">
            {spec.domain && (
              <div className="sw-spec__group">
                <span className="sw-spec__label">Domain</span>
                <span className="sw-spec__domain-tag">
                  <IconGlobe /> {spec.domain}
                </span>
              </div>
            )}
            <div className="sw-spec__group">
              <span className="sw-spec__label">Summary</span>
              <span
                className={
                  'sw-spec__value' + (spec.summary ? '' : ' sw-spec__value--placeholder')
                }
              >
                {spec.summary || placeholder}
              </span>
            </div>
            {spec.priority && (
              <div className="sw-spec__group">
                <span className="sw-spec__label">Priority</span>
                <span className="sw-spec__priority">{spec.priority}</span>
              </div>
            )}
            {spec.fields &&
              Object.keys(spec.fields).map((k) => (
                <div className="sw-spec__group" key={k}>
                  <span className="sw-spec__label">{k}</span>
                  <span
                    className={
                      'sw-spec__value' +
                      (spec.fields && spec.fields[k]
                        ? ''
                        : ' sw-spec__value--placeholder')
                    }
                  >
                    {(spec.fields && spec.fields[k]) || placeholder}
                  </span>
                </div>
              ))}
            <div className="sw-spec__divider" />
            <div className="sw-spec__group">
              <span className="sw-spec__label">Target project</span>
              <span
                className={
                  'sw-spec__value' +
                  (spec.targetProject ? '' : ' sw-spec__value--placeholder')
                }
              >
                {spec.targetProject || placeholder}
              </span>
            </div>
            <div className="sw-spec__group">
              <span className="sw-spec__label">Reviewer</span>
              <span
                className={
                  'sw-spec__value' + (spec.reviewer ? '' : ' sw-spec__value--placeholder')
                }
              >
                {spec.reviewer || placeholder}
              </span>
            </div>
          </div>
        </div>
        <div className="sw-preview__footer">
          <button
            className={
              'sw-preview__submit' + (canSubmit ? '' : ' sw-preview__submit--disabled')
            }
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {phase === 'SUBMITTING' ? (
              'Submitting...'
            ) : (
              <>
                <IconCheck /> Submit for Review
              </>
            )}
          </button>
        </div>
      </>
    );
  };

  const previewStatus = () => {
    if (phase === 'CONFIRMED') return { text: 'Submitted', cls: 'ready' };
    if (!spec) return { text: 'Waiting', cls: 'empty' };
    if (spec.status === 'ready') return { text: 'Ready', cls: 'ready' };
    return { text: 'Building...', cls: 'building' };
  };
  const ps = previewStatus();

  return (
    <div className="sw-page">
      <div className="sw-main">
        <div className="sw-chat">
          <div className="sw-chat__header">
            <div className="sw-chat__ai-icon"><IconSpec /></div>
            <div className="sw-chat__ai-info">
              <div className="sw-chat__ai-name">{ASSISTANT_NAME}</div>
              <div className="sw-chat__ai-status">Online</div>
            </div>
          </div>
          <div className="sw-messages" ref={messagesRef}>
            {messages.map((msg, i) => (
              <div
                key={i}
                className={'sw-msg ' + (msg.type === 'ai' ? 'sw-msg--ai' : 'sw-msg--user')}
              >
                <div className="sw-msg__avatar">
                  {msg.type === 'ai' ? <IconSpec /> : (computeInitials(user.name) || 'U')}
                </div>
                <div className="sw-msg__bubble">
                  {msg.domain && <span className="sw-msg__domain-badge">{msg.domain}</span>}
                  {renderMessageContent(msg)}
                  {msg.files && msg.files.length > 0 && (
                    <div className="sw-msg__attachments">
                      {msg.files.map((f) => (
                        <div className="sw-attachment" key={f.fileId}>
                          <div className="sw-attachment__icon"><IconFile /></div>
                          <div className="sw-attachment__info">
                            <span className="sw-attachment__name">{f.fileName}</span>
                            <span className="sw-attachment__size">
                              {formatFileSize(f.fileSize)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="sw-typing">
                <div className="sw-typing__avatar"><IconSpec /></div>
                <div className="sw-typing__bubble">
                  <span className="sw-typing__dot" />
                  <span className="sw-typing__dot" />
                  <span className="sw-typing__dot" />
                </div>
              </div>
            )}
            {phase === 'ERROR' && errorMessage && (
              <div className="sw-error">
                <div className="sw-error__avatar"><IconWarning /></div>
                <div className="sw-error__bubble">
                  <div className="sw-error__title">
                    <IconWarning /> Something went wrong
                  </div>
                  <span>{errorMessage}</span>
                  <button className="sw-error__retry" onClick={handleRetry}>
                    Try again
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="sw-input">
            {attachedFiles.length > 0 && (
              <div className="sw-input__attachments">
                {attachedFiles.map((f, i) => (
                  <div className="sw-input__file-chip" key={i}>
                    <IconFile />
                    <span>{f.name}</span>
                    <span>({formatFileSize(f.size)})</span>
                    <button
                      className="sw-input__file-remove"
                      onClick={() => removeAttachment(i)}
                      title="Remove"
                    >
                      <IconClose />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="sw-input__row">
              <button
                className="sw-input__attach"
                onClick={handleAttachClick}
                title={`Attach a file (${ALLOWED_TYPES.join(', ')})`}
              >
                <IconAttach />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="sw-hidden"
                onChange={handleFilesSelected}
                accept={ALLOWED_TYPES.join(',')}
                multiple
              />
              <textarea
                ref={textareaRef}
                className="sw-input__field"
                placeholder="Describe what you need..."
                value={inputValue}
                onChange={handleTextareaInput}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                rows={1}
              />
              <button
                className={
                  'sw-input__send' + (canSend ? '' : ' sw-input__send--disabled')
                }
                onClick={handleSend}
                disabled={!canSend}
                title="Send"
              >
                <IconSend />
              </button>
            </div>
          </div>
        </div>

        <div className="sw-preview">
          <div className="sw-preview__header">
            <div className="sw-preview__title">Spec Preview</div>
            <span className={`sw-preview__status sw-preview__status--${ps.cls}`}>
              {ps.text}
            </span>
          </div>
          {renderPreviewBody()}
        </div>
      </div>
    </div>
  );
};

export default SpecWriter;
